import { useEffect, useRef } from 'react';

import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { Dialog } from '@/components/Dialog';
import Markdown from '@/components/Markdown';
import downloadTextFileFromDatalake from '@/utils/downloadTextFileFromDatalake';

import { FeedArticleDialogSkeleton } from './FeedArticleDialogSkeleton';

type FeedArticleDialogProps = {
  open: boolean;
  CTAValue: string;
  onClose: () => void;
};

export const FeedArticleDialog = ({ open, CTAValue, onClose }: FeedArticleDialogProps) => {
  const [groupName, fileName] = CTAValue.split('/');
  const posthog = usePostHog();
  const contentReadyAt = useRef<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['datalake', 'file', groupName, fileName],
    enabled: open,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => downloadTextFileFromDatalake(fileName, groupName)
  });

  useEffect(() => {
    if (open && !isLoading && !error && data) {
      contentReadyAt.current = Date.now();
    }
  }, [open, isLoading, error, data]);

  const handleClose = () => {
    if (contentReadyAt.current !== null) {
      posthog?.capture('feed_article_reading_time', {
        file_name: fileName,
        group_name: groupName,
        reading_time_seconds: Math.round((Date.now() - contentReadyAt.current) / 1000)
      });
      contentReadyAt.current = null;
    }
    onClose();
  };

  return (
    <Dialog
      closeIcon
      open={open}
      sx={{ minWidth: 800 }}
      onClose={handleClose}
    >
      {isLoading && <FeedArticleDialogSkeleton />}
      {error && <Typography color='error'>"error"</Typography>}
      {!isLoading && !error && data && (
        <Typography
          component='div'
          sx={{
            overflowWrap: 'break-word',
            hyphens: 'auto',
            fontSize: '14px',
            marginTop: '-38px'
          }}
        >
          <Markdown>{data}</Markdown>
        </Typography>
      )}
    </Dialog>
  );
};
